import { useRef, useState } from 'react';

import { usePayerAddress } from 'lib/hooks/queries/payers/usePayerAddress';
import { usePayers } from 'lib/hooks/queries/payers/usePayers';

import Button from 'components/shared/Button/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Loader from 'components/shared/Loader/Loader';
import Modal from 'components/shared/Modal/Modal';

import { PayerInitialValues } from '../lib/helper';
import useOpenModal from '../lib/useOpenModal';

import PayerForm from './PayerForm/PayerForm';
import { usePayerForm } from './PayerForm/lib/usePayerForm';

const PayerFormBody = ({ practiceId, isFormLoading, formikRef, payerFormRef }) => {
  const openModal = useOpenModal();

  return (
    <div className="flex h-full flex-col gap-4">
      <PayerForm
        ref={payerFormRef}
        isUpdate
        practiceId={practiceId}
        isLoading={isFormLoading}
        openModal={openModal}
        formik={formikRef.current}
      />
    </div>
  );
};

const PayerModal = ({ payerId, isOpen, handleClose, practiceId }) => {
  const payerFormRef = useRef();
  const formikRef = useRef();

  const [defaultAddress, setDefaultAddress] = useState(false);

  const { data: payersData, isLoading: isLoadingPayer } = usePayers({
    params: { id: payerId, practiceId },
    dependencies: [payerId],
    options: { enabled: !!payerId }
  });

  const payer = payersData?.payers?.[0] || null;

  const { data } = usePayerAddress({
    params: { payerId: payer?.id, fallbackToDefaultAddress: defaultAddress },
    dependencies: [payer?.id, defaultAddress],
    options: { enabled: !!payer?.id && defaultAddress }
  });

  const addressData = data || payer?.practicePayers?.[0]?.payerAddress;
  const initialValues = PayerInitialValues(payer, addressData);

  const { formik, isLoading: isFormLoading } = usePayerForm({
    payer,
    practiceId,
    externalInitialValues: initialValues,
    enableReinitialize: true,
    onSuccess: handleClose
  });

  formikRef.current = formik;

  const showLoadDefaultAddress =
    payer?.address && !payer?.practicePayers?.[0]?.address_id && !isLoadingPayer;

  const customHeader = (
    <div className="flex w-full items-center justify-between gap-4">
      <p className="whitespace-nowrap text-sm font-medium text-neutral-800">
        {payer?.name || 'Update Payer'}
      </p>
      {showLoadDefaultAddress && (
        <Button
          variant="secondary"
          size="small"
          color="neutral"
          onClick={() => setDefaultAddress(true)}
          text="Load Default Address"
          data-qa="load-default-address-button"
          className="ml-auto mr-6 h-6"
        />
      )}
    </div>
  );

  const footer = isLoadingPayer ? null : (
    <div className="flex w-full items-center justify-end gap-2 bg-white">
      <Button variant="tertiary" color="neutral" text="Cancel" onClick={handleClose} size="small" />
      <Button
        variant="primary"
        text="Update payer"
        onClick={() => payerFormRef.current?.submitForm()}
        isLoading={isFormLoading}
        isDisabled={!formik?.dirty}
        id="savePayerModalBtn"
        data-qa="create-payer-btn"
        size="small"
      />
    </div>
  );

  if (!payer && !isLoadingPayer) {
    return null;
  }

  return (
    <Modal
      width="medium"
      headerClassName="px-5 py-3"
      isOpen={isOpen}
      handleClose={handleClose}
      customHeader={customHeader}
      footer={footer}>
      {isLoadingPayer ? (
        <div className="flex h-full w-full items-center justify-center">
          <Loader color="primary" size={48} />
        </div>
      ) : (
        <PayerFormBody
          practiceId={practiceId}
          isFormLoading={isFormLoading}
          formikRef={formikRef}
          payerFormRef={payerFormRef}
        />
      )}
    </Modal>
  );
};

export default withErrorBoundary(PayerModal);
